import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BarChartComponent } from './components/bar-chart/bar-chart.component';
import { Solution } from '../solutions/models/solutions.types';
import { RecommendationComponent } from './components/recommendation/recommendation.component';
import { ComparisonTableComponent } from './components/comparison-table/comparison-table.component';
import { AsyncState, getData, getError } from '../../shared/models/async-state';
import { EmptyStateComponent } from '../../shared/components/empty-state.component';
import { LoadingSkeletonComponent } from '../../shared/components/loading-skeleton.component';
import { ErrorStateComponent } from '../../shared/components/error-state.component';
import { BarSeries, Charts } from './models/comparisson.types';
import { Period, PERIOD_DISPLAY_LABELS } from '../../core/constants/periods';
import { formatChemicalUnit } from '../../shared/utils/format-unit';

@Component({
  selector: 'app-comparison',
  imports: [
    BarChartComponent,
    RecommendationComponent,
    CommonModule,
    ComparisonTableComponent,
    LoadingSkeletonComponent,
    ErrorStateComponent,
    EmptyStateComponent,
  ],
  templateUrl: './comparison.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparisonComponent {
  readonly dataState = input.required<AsyncState<Solution[]>>();
  readonly selectedPeriod = input<Period>('DAY');
  readonly retry = output<void>();

  readonly data = computed(() => getData(this.dataState()));
  readonly error = computed(() => getError(this.dataState()));

  readonly selectedPeriodLabel = computed(() => {
    return PERIOD_DISPLAY_LABELS[this.selectedPeriod()];
  });

  protected readonly solutions = computed(() => {
    return this.data() ?? [];
  });

  protected readonly isEmpty = computed(() => {
    return this.data() && this.solutions().length === 0;
  });

  protected readonly recommendation = computed(() => {
    const solutions = this.solutions();
    const greener = solutions.find(s => s.isGreener);
    return greener ? String(greener.name) : 'No recommendation available';
  });

  protected readonly charts = computed(() => {
    const solutions = this.solutions();
    return this.buildCharts(solutions);
  });

  onRetry(): void {
    this.retry.emit();
  }

  private buildCharts(solutions: Solution[]): Charts {
    const categories = solutions.map(s => String(s.name));

    const currencyUnit = solutions.find(s => s.kpis.currencyUnit)?.kpis.currencyUnit ?? '';
    const emissions = formatChemicalUnit(solutions.find(s => s.kpis.emissionUnit)?.kpis.emissionUnit) || 'kgCO₂';
    const pollutantUnit = formatChemicalUnit(solutions.find(s => s.kpis.pollutantUnit)?.kpis.pollutantUnit) || 'kg';

    const rental = solutions.map(s => s.kpis.rentalFeeTotal ?? 0);
    const energy = solutions.map(s => s.kpis.energyCostTotal ?? 0);

    const totalCostSeries: BarSeries[] = [
      { name: 'Rental fee', data: rental, stack: 'total' },
      { name: 'Energy cost', data: energy, stack: 'total' },
    ];

    const totalCo2Series: BarSeries[] = [
      {
        name: 'CO₂e',
        data: solutions.map(s => s.kpis.co2TotalKg ?? 0),
      },
    ];

    const pollutantsSeries: BarSeries[] = [
      {
        name: 'NOₓ',
        data: solutions.map(s => s.kpis.noxKg ?? 0),
        stack: 'pollutants',
      },
      {
        name: 'PM',
        data: solutions.map(s => s.kpis.pmKg ?? 0),
        stack: 'pollutants',
      },
    ];

    return {
      categories,
      currencyUnit,
      co2Unit: emissions,
      pollutantUnit,
      totalCostSeries,
      totalCo2Series,
      pollutantsSeries,
    };
  }
}
